import * as m from '@staiter/i18n';
import type { MemoryEntry } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Brain, Clock } from 'lucide-react';
import { EmptyState, PageHeader, Table } from '~/components/data';
import { Button } from '~/components/primitives';
import { fetchMemory, memoryAction } from '~/lib/api/admin';
import shared from './_auth._shared.module.css';
import styles from './_auth.admin.module.css';

export const Route = createFileRoute('/_auth/admin/memory')({
  staticData: { minMainWidth: 960 },
  component: MemoryAdmin,
});

function fmt(ts: string | null): string {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

function MemoryAdmin(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'memory'], queryFn: fetchMemory });
  const items: MemoryEntry[] = data?.items ?? [];
  const stats = data?.stats;

  const actionMut = useMutation({
    mutationFn: (input: { id: string; action: 'promote' | 'demote' | 'expire' }) =>
      memoryAction(input.id, input.action),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'memory'] }),
  });

  // FE-267/275 Pass 5: единый EmptyState вместо тройной empty (3 nulled stat tiles + empty table)
  // когда нет данных. EmptyState даёт meaningful guidance + agent identity icon.
  const isEmpty = !isLoading && items.length === 0;
  if (isEmpty) {
    return (
      <div className={shared.page}>
        <PageHeader
          title={m.route_admin_memory_title()}
          description={m.route_admin_memory_description()}
        />
        <EmptyState
          title={m.route_admin_memory_empty_title()}
          description={m.route_admin_memory_empty_description()}
          icon={<Brain size={32} />}
        />
      </div>
    );
  }

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.route_admin_memory_title()}
        description={m.route_admin_memory_description()}
      />

      <div className={styles.statsGrid}>
        <div className={styles.statTile}>
          <div className={styles.statLabel}>{m.route_admin_memory_stat_total()}</div>
          <div className={styles.statValue}>{items.length}</div>
        </div>
        <div className={styles.statTile}>
          <div className={styles.statLabel}>{m.route_admin_memory_stat_avg_age()}</div>
          <div className={styles.statValue}>
            {stats?.avg_age_days !== null && stats?.avg_age_days !== undefined
              ? stats.avg_age_days.toFixed(1)
              : '—'}
          </div>
        </div>
        <div className={styles.statTile}>
          <div className={styles.statLabel}>{m.route_admin_memory_stat_recall_hit()}</div>
          <div className={styles.statValue}>
            {stats?.recall_hit_rate !== null && stats?.recall_hit_rate !== undefined
              ? `${(stats.recall_hit_rate * 100).toFixed(1)}%`
              : '—'}
          </div>
        </div>
        {stats &&
          Object.entries((stats.by_scope ?? {}) as Record<string, number>).map(([scope, count]) => (
            <div key={scope} className={styles.statTile}>
              <div className={styles.statLabel}>{m.route_admin_memory_stat_scope({ scope })}</div>
              <div className={styles.statValue}>{count}</div>
            </div>
          ))}
      </div>

      <Table
        ariaLabel={m.route_admin_memory_table_aria()}
        loading={isLoading}
        data={items}
        rowKey={(mm) => mm.id}
        columns={[
          {
            id: 'scope',
            header: m.route_admin_memory_col_scope(),
            accessor: (mm) => <code>{mm.scope}</code>,
          },
          {
            id: 'lifecycle',
            header: m.route_admin_memory_col_lifecycle(),
            accessor: (mm) => mm.lifecycle,
          },
          {
            id: 'sensitivity',
            header: m.route_admin_memory_col_sensitivity(),
            accessor: (mm) => mm.sensitivity,
          },
          {
            id: 'provenance',
            header: m.route_admin_memory_col_provenance(),
            accessor: (mm) => mm.provenance,
          },
          {
            id: 'fresh',
            header: m.route_admin_memory_col_fresh_until(),
            accessor: (mm) => <span className={styles.mono}>{fmt(mm.fresh_until)}</span>,
          },
          {
            id: 'v',
            header: m.route_admin_memory_col_version(),
            accessor: (mm) => <span className={styles.mono}>v{mm.version}</span>,
          },
          {
            id: 'summary',
            header: m.route_admin_memory_col_summary(),
            accessor: (mm) => <span className={styles.muted}>{mm.summary}</span>,
          },
          {
            id: 'actions',
            header: '',
            accessor: (mm) => (
              <div className={shared.rowWrap}>
                <Button
                  size="sm"
                  variant="ghost"
                  leadingIcon={<ArrowUp size={12} />}
                  onClick={() => actionMut.mutate({ id: mm.id, action: 'promote' })}
                  disabled={actionMut.isPending}
                >
                  {m.route_admin_memory_btn_promote()}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  leadingIcon={<ArrowDown size={12} />}
                  onClick={() => actionMut.mutate({ id: mm.id, action: 'demote' })}
                  disabled={actionMut.isPending}
                >
                  {m.route_admin_memory_btn_demote()}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  leadingIcon={<Clock size={12} />}
                  onClick={() => actionMut.mutate({ id: mm.id, action: 'expire' })}
                  disabled={actionMut.isPending}
                >
                  {m.route_admin_memory_btn_expire()}
                </Button>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
